// ============================================================
// ClientActivityFeed.tsx
// The client ACTIVITY tab — the BILLABLE timeline — built to the approved
// mockup (outputs/casevo-client-activity-timeline.html, "CA40D9_1"):
//   • Layout A · rich cards (DEFAULT) and Layout B · rail ledger (toggle,
//     remembered per browser). Both render from ONE entry-type registry
//     (constants/entryTypes.ts) — icon, tone, filter family, bills.
//   • KPI tiles, type-family segments (future families greyed "Soon"),
//     date range / case / person / correspondent / search, drafts strip,
//     date grouping (Today / Yesterday / This week / This month / month /
//     year) with per-group billable subtotals, footer total.
//   • Billable entries: £ + time + units + Unbilled WIP / Billed (lock).
//     Non-billable entries (documents): no money — never "£0.00".
//     Grade + hourly rate: ADMINS ONLY.
//   • Billing entries — issued bills, payments received, credit notes — come
//     from GET /api/bill-events (useBillEvents: a read-time merge of the bill
//     tables, no Activity rows), with their £ masked server-side
//     (lib/billMoneyVisibility). They sit under All / Invoices, feed the
//     "Invoiced this month" tile, and open their bill on click (utils/openBill).
//   • Future types (generated docs, case updates, SMS, fax) are NOT built —
//     they appear as disabled "Soon" placeholders in the filter segments, the
//     Log-time popover and a "Coming to Activity" strip.
// Presentational: all data + mutations are owned by ClientSearchView (the
// same state/handlers the previous timeline used) and passed in.
// ============================================================
import React, { useCallback, useMemo, useState } from 'react'
import { SKELETON_BAR } from '../../hooks/useLoadPhase'
import type { Activity } from '../../types/activity.types'
import type { DocumentSearchResult } from '../../types/documentSearch.types'
import type { EmailDraft } from '../../api/casevoApi'
import type { BillEvent } from '../../types/billing.types'
import { partialMoney } from '../billing/billingUi'
import { openBillById } from '../../utils/openBill'
import { ACTION_TYPES } from '../../constants/actionTypes.constants'
import {
  ENTRY_FAMILIES, FUTURE_ENTRY_TYPES, TONES, entryTypeFor, resolveEntryTone,
  type EntryFamily, type EntryTypeDef,
} from '../../constants/entryTypes'
import { billableUnits, unitsLabel } from '../../utils/billingUnits'
import { formatFileSize } from '../shared/DocumentRow'
import UserAvatar from '../shared/UserAvatar'
import WithheldAmount from '../shared/WithheldAmount'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useCanDeleteDocument } from '../../utils/documentPermissions'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { BTN_PRIMARY } from '../../styles/buttons'
import { EntryIconSvg, UiIconSvg } from './activityIcons'
import type { ActionVariantKey } from '../shared/AddActivityModal'
import { useCaseSectionFrame } from '../case/sections/sectionKit'
import { LogTimeButton } from '../shared/ActivityTypePicker'
import { openFileById } from '../../utils/documentAccess'
import { ErrorNote } from '../shared/ActionBox'
import { PROOF_LOCKED } from '../shared/EsignedTag'

// ── mockup classes ──────────────────────────────────────────
const CARD =
  'bg-casevo-surface border border-casevo-border rounded-[14px] ' +
  'shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
const BTN = 'inline-flex items-center gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px] font-medium border whitespace-nowrap transition-colors focus:outline-none casevo-focus'
const BTN_SECONDARY = `${BTN} border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid`
const FLD = 'inline-flex items-center gap-[7px] h-8 px-[10px] rounded-[9px] border border-casevo-border bg-casevo-input text-[13px] text-casevo-forest'
const SELECT = 'bg-transparent border-0 outline-none text-[13px] text-casevo-forest pr-1 cursor-pointer appearance-none'
const TPILL = 'inline-flex items-center gap-[5px] whitespace-nowrap text-[10.5px] font-bold tracking-[0.1em] uppercase px-[7px] py-[2px] rounded-[6px]'
const CHIP_CASE = 'inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] bg-casevo-mint text-[12px] font-medium text-casevo-forest dark:text-[#cfeedd] whitespace-nowrap max-w-full'
const ST = 'inline-flex items-center gap-[5px] text-[11px] font-semibold px-2 py-[2px] rounded-full whitespace-nowrap'
const ST_WIP = `${ST} bg-casevo-border-soft text-casevo-text-soft`
const ST_BILLED = `${ST} bg-casevo-mint text-casevo-brand`
// Case-less time — saved, but never billable (red, so it reads as a problem to fix).
const ST_NOT_BILLABLE = `${ST} bg-[#fdecec] text-casevo-danger dark:bg-[rgba(127,29,29,0.35)] dark:text-[#fca5a5]`
const IBTN = 'w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-colors focus:outline-none casevo-focus disabled:opacity-50'
// Documents on a case the viewer can't open (search returns them show-but-lock;
// the server's download-url gate would refuse them anyway).
const LOCKED_DOC_TIP = "This document is on a case you don't have access to"
const isLockedDoc = (d: DocumentSearchResult): boolean => d.caseAccessible === false
const SOON = 'text-[9.5px] font-bold tracking-[0.08em] uppercase border border-dashed border-casevo-border rounded-[5px] px-1 text-casevo-text-soft'

// ── helpers ─────────────────────────────────────────────────
type Range = 'all' | 'month' | '30d' | 'year'
const RANGES: { key: Range; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: 'month', label: 'This month' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'year', label: 'This year' },
]
const money = (pence: number): string =>
  `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const mins = (s: number): string => {
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`
}
// Units are whole 6-minute units ROUNDED UP per entry (billableUnits — the
// same rule the server charges). Totals SUM the per-entry units, so a group
// subtotal / footer / KPI always equals the units printed on its rows (and
// the units actually charged) — never ceil(summed seconds). £ is always
// summed from each entry's integer amountPence — never recomputed.
const unitsOf = (s: number): number => billableUnits(s)
const fmtUnits = (t: number): string => String(t)
const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
const startOfWeek = (d: Date): number => {
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7)) // Monday
  return s.getTime()
}
const startOfMonth = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), 1).getTime()
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
// Same buckets as Document search (DocumentSearchView dateBucket).
type Bucket = 'today' | 'yesterday' | 'week' | 'month' | 'older'
const bucketOf = (iso: string, now: Date): { key: string; title: string; kind: Bucket } => {
  const d = new Date(iso)
  const t = d.getTime()
  if (t >= startOfDay(now)) return { key: 'today', title: 'Today', kind: 'today' }
  // Calendar arithmetic (not −24h) so the day after a clock change is still right.
  if (t >= new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime()) return { key: 'yesterday', title: 'Yesterday', kind: 'yesterday' }
  if (t >= startOfWeek(now)) return { key: 'week', title: 'This week', kind: 'week' }
  if (t >= startOfMonth(now)) return { key: 'month', title: 'This month', kind: 'month' }
  const y = d.getFullYear()
  if (y >= now.getFullYear() - 1) return { key: `m:${y}-${d.getMonth()}`, title: `${MONTHS[d.getMonth()]} ${y}`, kind: 'older' }
  return { key: `y:${y}`, title: String(y), kind: 'older' }
}
const whenLabel = (iso: string, kind: Bucket): string => {
  const d = new Date(iso)
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  if (kind === 'today' || kind === 'yesterday') return hm
  if (kind === 'week') return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${hm}`
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) })
}
const extOf = (name: string): string => {
  const m = /\.([a-z0-9]{1,5})$/i.exec(name)
  return m ? m[1].toUpperCase() : 'File'
}
// ★ Billable = a billable TYPE (time / email) AND linked to a case. Every
// bill is raised on one case and draws its WIP from that case's entries
// (server-side, case-scoped), so a case-less entry is a non-billable record:
// it shows here, marked "Not billable", but never counts as Billable / WIP.
const isBillable = (a: Activity, def: EntryTypeDef): boolean => def.bills && !!a.caseId
const isCaselessTime = (a: Activity, def: EntryTypeDef): boolean => def.bills && !a.caseId && a.durationSeconds > 0
const NOT_BILLABLE_TIP = 'Not linked to a case, so this time isn’t billable — edit the entry and link a case to bill it'
// A billing entry's title (no £ — the figure sits in the billing slot).
const billTitle = (b: BillEvent): string => {
  const what = b.billType === 'ON_ACCOUNT' ? 'Request for payment on account' : 'Bill'
  if (b.kind === 'payment') return `Payment received on ${b.number ?? 'a bill'}`
  if (b.kind === 'credit_note') return `Credit note ${b.number ?? ''} on ${b.billNumber ?? 'a bill'}`.replace(/\s+/g, ' ')
  return `${what} ${b.number ?? ''} issued${b.status === 'VOID' ? ' (since voided)' : ''}`.replace(/\s+/g, ' ')
}
// The £ on a billing entry, as masked server-side: hidden (null) → the lock;
// a partial bill total → the lock + "partial — some items hidden" (a £0 — none
// of it yours — is the lock alone). Never a bare complete-looking figure.
const billMoneyNode = (b: BillEvent, size?: number): React.ReactNode => partialMoney(b.amountPence, b.amountPartial, size)

type FeedEntry =
  | { kind: 'activity'; id: string; at: string; def: EntryTypeDef; a: Activity }
  | { kind: 'document'; id: string; at: string; def: EntryTypeDef; d: DocumentSearchResult }
  // An issued bill / payment received / credit note (GET /api/bill-events — a
  // read-time merge; not an Activity row). Its £ arrives masked server-side.
  | { kind: 'bill'; id: string; at: string; def: EntryTypeDef; b: BillEvent }

type LayoutMode = 'cards' | 'ledger'
const LAYOUT_KEY = 'casevo.activityLayout'
const readLayout = (): LayoutMode => {
  try { return window.localStorage.getItem(LAYOUT_KEY) === 'ledger' ? 'ledger' : 'cards' } catch { return 'cards' }
}

export interface ActivityCaseOption { id: string; name: string; ref: string }

export interface ClientActivityFeedProps {
  clientFirstName: string
  // Server-filtered list (type / case / correspondent), null = loading.
  activities: Activity[] | null
  // Unfiltered list — KPI tiles + correspondent options. Null → falls back.
  activitiesAll: Activity[] | null
  documents: DocumentSearchResult[] | null
  // Billing on the timeline (useBillEvents): issued bills, payments received,
  // credit notes — money already masked for this viewer. Null = loading (the
  // rest of the feed doesn't wait for it).
  billEvents: BillEvent[] | null
  refetching: boolean
  errors: (string | null)[]
  isAdmin: boolean
  currentUserId: string | null
  cases: ActivityCaseOption[]
  // Server-side filters (unchanged handlers from the previous timeline).
  typeFilter: string
  onTypeFilter: (v: string) => void
  caseFilter: string
  onCaseFilter: (v: string) => void
  corrFilter: string | null
  onCorrFilter: (v: string | null) => void
  onClearServerFilters: () => void
  // Drafts strip. draftsLoading: the drafts fetch hasn't answered yet — the
  // feed waits for it (with activities / documents / billing) so the strip
  // can't pop in above the list afterwards.
  drafts: EmailDraft[]
  draftsLoading?: boolean
  onResumeDraft: (d: EmailDraft) => void
  onDeleteDraft: (d: EmailDraft) => void
  // Row actions.
  canModify: (a: Activity) => boolean
  deletingActivityId: string | null
  downloadingDocId: string | null
  deletingDocId: string | null
  onViewActivity: (a: Activity) => void
  onEditActivity: (a: Activity) => void
  onDeleteActivity: (a: Activity) => void
  onOpenDocument: (d: DocumentSearchResult) => void
  onDownloadDocument: (d: DocumentSearchResult) => void
  onDeleteDocument: (d: DocumentSearchResult) => void
  onOpenCase: (caseId: string) => void
  // Create actions.
  onNewActivity: () => void
  onLogVariant: (v: ActionVariantKey) => void
  // The ONE "Invoice" action in the Log-time menu (raises a bill). On the case
  // page it's for this case; on the client-wide tab the parent asks which case.
  onCreateInvoice?: () => void
  // Client-wide tab only: a statement of the client's issued bills (ClientStatementModal).
  onStatement?: () => void
  onSendEmail: () => void
  onUpload: () => void
  // Stage 4 (case-sections batch 3): the SAME feed mounted on the CASE page,
  // scoped to one case. Present → the parent loads this case's activities +
  // documents only; the case filter is fixed to "This case" (no select), the
  // per-entry case chip is hidden (every entry is this case), the header
  // reads for a case (frame eyebrow + Send email / Log time) and the
  // Cards/Ledger toggle sits in the toolbar. Absent → client page, unchanged.
  caseScope?: { caseId: string }
}


const ClientActivityFeed: React.FC<ClientActivityFeedProps> = (p) => {
  const scoped = !!p.caseScope
  const frame = useCaseSectionFrame()
  const firmScheme = useCasevoStore((s) => s.firmColourScheme)
  const userScheme = useCasevoStore((s) => s.userColourScheme)
  const canDeleteDoc = useCanDeleteDocument() // (Batch 4) uploader / admin / deleteOthersDocuments
  const liveName = useLiveUserName()
  const tone = useCallback((key: string) => TONES[resolveEntryTone(key, firmScheme, userScheme)], [firmScheme, userScheme])

  const [layout, setLayout] = useState<LayoutMode>(readLayout)
  const chooseLayout = (m: LayoutMode): void => {
    setLayout(m)
    try { window.localStorage.setItem(LAYOUT_KEY, m) } catch { /* private mode — session only */ }
  }
  const [family, setFamily] = useState<'all' | EntryFamily>('all')
  const [range, setRange] = useState<Range>('all')
  const [person, setPerson] = useState('')
  const [q, setQ] = useState('')
  const [pdfError, setPdfError] = useState<{ id: string; msg: string } | null>(null)

  const caseById = useMemo(() => new Map(p.cases.map((c) => [c.id, c])), [p.cases])
  const all = useMemo(() => p.activitiesAll ?? p.activities ?? [], [p.activitiesAll, p.activities])
  const docs = useMemo(() => p.documents ?? [], [p.documents])
  const bills = useMemo(() => p.billEvents ?? [], [p.billEvents])

  // Switching family: a server type filter outside the new family resets.
  const pickFamily = (f: 'all' | EntryFamily): void => {
    setFamily(f)
    if (p.typeFilter !== 'all' && f !== 'all' && entryTypeFor(p.typeFilter).family !== f) p.onTypeFilter('all')
  }

  // ── the visible entries (server filters already applied upstream) ──
  const entries = useMemo<FeedEntry[]>(() => {
    const now = new Date()
    const from = range === 'month' ? startOfMonth(now) : range === '30d' ? now.getTime() - 30 * 86400000 : range === 'year' ? new Date(now.getFullYear(), 0, 1).getTime() : -Infinity
    const needle = q.trim().toLowerCase()
    const out: FeedEntry[] = []
    for (const a of p.activities ?? []) {
      const def = entryTypeFor(a.actionType)
      if (family !== 'all' && def.family !== family) continue
      if (new Date(a.createdAt).getTime() < from) continue
      if (person && a.actorUserId !== person) continue
      if (needle && ![a.action, a.content, a.correspondentName, a.correspondentType, a.actorName].join(' ').toLowerCase().includes(needle)) continue
      out.push({ kind: 'activity', id: `a:${a.id}`, at: a.createdAt, def, a })
    }
    // Documents show under All / Documents only, and — as before — only when
    // no action-type or correspondent filter is applied.
    const docsOn = (family === 'all' || family === 'docs') && p.typeFilter === 'all' && p.corrFilter === null
    if (docsOn) {
      for (const d of docs) {
        if (p.caseFilter && d.caseId !== p.caseFilter) continue
        if (new Date(d.uploadedAt).getTime() < from) continue
        if (person && d.uploadedBy?.id !== person) continue
        if (needle && !d.filename.toLowerCase().includes(needle)) continue
        out.push({ kind: 'document', id: `d:${d.id}`, at: d.uploadedAt, def: entryTypeFor('document'), d })
      }
    }
    // Billing entries — like documents, under All / Invoices only, and only
    // when no action-type or correspondent filter is applied.
    const billsOn = (family === 'all' || family === 'bill') && p.typeFilter === 'all' && p.corrFilter === null
    if (billsOn) {
      for (const b of bills) {
        if (p.caseFilter && b.caseId !== p.caseFilter) continue
        if (new Date(b.at).getTime() < from) continue
        if (person && b.actor?.id !== person) continue
        if (needle && ![billTitle(b), b.number, b.billNumber, b.method].join(' ').toLowerCase().includes(needle)) continue
        out.push({ kind: 'bill', id: b.id, at: b.at, def: entryTypeFor(b.kind), b })
      }
    }
    return out.sort((x, y) => new Date(y.at).getTime() - new Date(x.at).getTime())
  }, [p.activities, docs, bills, family, range, person, q, p.typeFilter, p.corrFilter, p.caseFilter])

  const groups = useMemo(() => {
    const now = new Date()
    const order: { key: string; title: string; kind: Bucket; items: FeedEntry[] }[] = []
    const map = new Map<string, (typeof order)[number]>()
    for (const e of entries) {
      const b = bucketOf(e.at, now)
      let g = map.get(b.key)
      if (!g) { g = { ...b, items: [] }; map.set(b.key, g); order.push(g) }
      g.items.push(e)
    }
    return order
  }, [entries])

  const billSum = (list: FeedEntry[]): { secs: number; tenths: number; pence: number } => {
    let secs = 0
    let tenths = 0
    let pence = 0
    // Billable TIME only — billing entries (bills / payments / credits) aren't
    // added to the "Billable" subtotals.
    for (const e of list) if (e.kind === 'activity' && isBillable(e.a, e.def)) { secs += e.a.durationSeconds; tenths += unitsOf(e.a.durationSeconds); pence += e.a.amountPence ?? 0 }
    return { secs, tenths, pence }
  }
  const visibleTotal = billSum(entries)

  // ── KPIs (whole client, unfiltered) ──
  const kpi = useMemo(() => {
    const now = new Date()
    const som = startOfMonth(now)
    let mSecs = 0; let mTenths = 0; let mPence = 0; let mBilled = 0; let wip = 0
    let time = 0; let corr = 0
    let withheld = false
    for (const a of all) {
      const def = entryTypeFor(a.actionType)
      if (def.family === 'corr') corr++; else time++
      if (!isBillable(a, def)) continue // case-less time is a non-billable record — never WIP
      if (a.amountPence == null) withheld = true
      if (!a.billed) wip += a.amountPence ?? 0
      if (new Date(a.createdAt).getTime() >= som) { mSecs += a.durationSeconds; mTenths += unitsOf(a.durationSeconds); mPence += a.amountPence ?? 0; if (a.billed) mBilled += a.amountPence ?? 0 }
    }
    // ★ Invoiced this month (the same period as "Billable this month"): bills
    // of costs issued this calendar month — an on-account request isn't a bill
    // of costs (its money is credited on the next one, so counting both would
    // double it), and a voided request counts for nothing. Payments received
    // this month alongside. Summed from the server-MASKED figures: a partial
    // bill adds only your items and a hidden amount adds nothing — either
    // flags the tile partial, so it's never read as the full figure.
    let invPence = 0; let invCount = 0; let invPartial = false
    let paidPence = 0; let paidPartial = false
    for (const b of bills) {
      if (new Date(b.at).getTime() < som) continue
      if (b.kind === 'invoice' && b.billType !== 'ON_ACCOUNT' && b.status !== 'VOID') {
        invCount++
        if (b.amountPence == null || b.amountPartial) invPartial = true
        invPence += b.amountPence ?? 0
      } else if (b.kind === 'payment') {
        if (b.amountPence == null) paidPartial = true
        paidPence += b.amountPence ?? 0
      }
    }
    return {
      mSecs, mTenths, mPence, mBilled, wip, time, corr, docs: docs.length, bills: bills.length,
      count: all.length + docs.length + bills.length, withheld, invPence, invCount, invPartial, paidPence, paidPartial,
    }
  }, [all, docs, bills])

  // ── family counts (for the segment badges) — whole client ──
  const famCount = (f: 'all' | EntryFamily): number => {
    if (f === 'all') return kpi.count
    if (f === 'docs') return kpi.docs
    if (f === 'bill') return kpi.bills
    return all.filter((a) => entryTypeFor(a.actionType).family === f).length
  }

  const people = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of all) m.set(a.actorUserId, liveName(a.actorUserId, a.actorName))
    for (const d of docs) if (d.uploadedBy?.id) m.set(d.uploadedBy.id, liveName(d.uploadedBy.id, d.uploadedBy.name))
    for (const b of bills) if (b.actor) m.set(b.actor.id, liveName(b.actor.id, b.actor.name ?? ''))
    return Array.from(m.entries()).sort((x, y) => x[1].localeCompare(y[1]))
  }, [all, docs, bills, liveName])

  const corrOptions = useMemo(() => {
    const types = Array.from(new Set(all.map((a) => a.correspondentType).filter((t) => !!t && t.trim().length > 0))).sort()
    const hasUntagged = all.some((a) => !a.correspondentType || a.correspondentType.trim().length === 0)
    return { types, hasUntagged }
  }, [all])

  const clientFiltersOn = family !== 'all' || range !== 'all' || person !== '' || q.trim() !== ''
  const serverFiltersOn = p.typeFilter !== 'all' || (!scoped && p.caseFilter !== '') || p.corrFilter !== null
  const clearAll = (): void => {
    setFamily('all'); setRange('all'); setPerson(''); setQ('')
    if (serverFiltersOn) p.onClearServerFilters()
  }

  // ── people / avatar ──
  const personOf = (userId: string | null | undefined, fallback: string) => {
    const name = liveName(userId, fallback) || fallback || 'Unknown'
    const isMe = !!userId && userId === p.currentUserId
    // (The avatar itself: shared UserAvatar — the one app-wide rule.)
    return { name, isMe }
  }
  const who = (userId: string | null | undefined, fallback: string, withYou = true): React.ReactNode => {
    const w = personOf(userId, fallback)
    return (
      <>
        <UserAvatar userId={userId} fallbackName={w.name} size="feed" title={false} />
        <span className="truncate">{w.name}</span>
        {withYou && w.isMe && <span className="text-[10px] font-bold px-1.5 rounded-full bg-casevo-gold text-[#3a2e08] leading-[1.6]">You</span>}
      </>
    )
  }

  const caseChip = (caseId: string | null, fallback?: { name?: string | null; ref?: string | null; locked?: boolean }): React.ReactNode => {
    // Case-scoped: every entry is this case — no chip, no "Open case".
    if (scoped) return null
    if (!caseId) return <span className="inline-flex items-center h-6 px-2 rounded-[7px] border border-casevo-border text-[12px] text-casevo-text-soft">Client-level</span>
    const c = caseById.get(caseId)
    const name = c?.name ?? fallback?.name ?? 'Case'
    const ref = c?.ref ?? fallback?.ref ?? ''
    // A document on a case the viewer can't open: show the case, but locked.
    if (fallback?.locked) {
      return (
        <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-[7px] border border-dashed border-casevo-border text-[12px] text-casevo-text-soft whitespace-nowrap max-w-full" title={LOCKED_DOC_TIP}>
          <UiIconSvg icon="lock" size={12} /><span className="truncate">{name}</span>
        </span>
      )
    }
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); p.onOpenCase(caseId) }} className={`${CHIP_CASE} hover:ring-1 hover:ring-casevo-mid focus:outline-none casevo-focus`} title={`Open ${name}`}>
        <UiIconSvg icon="case" size={12} /><span className="truncate">{name}</span>{ref && <span className="text-[11px] font-normal text-casevo-text-soft tabular-nums">{ref}</span>}
      </button>
    )
  }
  const typePill = (def: EntryTypeDef): React.ReactNode => <span className={`${TPILL} ${tone(def.key)}`}>{def.label}</span>
  const entryTitle = (e: FeedEntry): string => (e.kind === 'activity' ? e.a.action : e.kind === 'document' ? e.d.filename : billTitle(e.b))
  // Who did it: the entry's author / the uploader / who issued the bill,
  // recorded the payment or issued the credit note.
  const byline = (e: FeedEntry, withYou = true): React.ReactNode => (e.kind === 'activity'
    ? who(e.a.actorUserId, e.a.actorName, withYou)
    : e.kind === 'document'
      ? who(e.d.uploadedBy?.id, e.d.uploadedBy?.name ?? 'Uploaded', withYou)
      : who(e.b.actor?.id, e.b.actor?.name ?? 'Billing', withYou))

  const detailLine = (e: FeedEntry): string => {
    if (e.kind === 'document') return `${extOf(e.d.filename)} · ${formatFileSize(e.d.sizeBytes)} · ${e.d.caseId ? 'case document' : 'client document'}`
    if (e.kind === 'bill') {
      const b = e.b
      if (b.kind === 'payment') return b.method ? `By ${b.method.toLowerCase()}` : ''
      if (b.kind === 'credit_note') return 'Reduces what’s owed on the bill'
      return b.billType === 'FINAL_STATUTE' ? 'Final statute bill' : b.billType === 'INTERIM_STATUTE' ? 'Interim statute bill' : 'Request for payment on account'
    }
    const a = e.a
    const corr = a.correspondentType && a.correspondentName ? `${a.correspondentType} · ${a.correspondentName}` : (a.correspondentName || a.correspondentType || '')
    return corr ? `With ${corr}` : ''
  }

  // Billing slot — billable → £ / time · units / (admin) grade · rate / status.
  // Non-billable → nothing at all (never "£0.00").
  const billSlot = (e: FeedEntry): React.ReactNode => {
    // A billing entry: its own £ (masked server-side) — not time / units / WIP.
    if (e.kind === 'bill') {
      const b = e.b
      return (
        <>
          <div className="text-[15px] font-semibold tracking-[-0.01em] text-casevo-forest tabular-nums">{b.kind === 'payment' || b.kind === 'credit_note' ? <>{b.amountPence != null && '−'}{billMoneyNode(b, 13)}</> : billMoneyNode(b, 13)}</div>
          <div className="text-[12px] text-casevo-text-soft">{b.kind === 'invoice' ? 'incl. VAT' : b.kind === 'payment' ? 'received' : 'credited'}</div>
        </>
      )
    }
    if (e.kind !== 'activity' || !e.def.bills) return null
    const a = e.a
    // Case-less time: a record, not a charge — time + units, no £, flagged.
    if (!a.caseId) {
      if (a.durationSeconds <= 0) return null
      return (
        <>
          <div className="text-[12px] text-casevo-text-soft tabular-nums">{mins(a.durationSeconds)} · {unitsLabel(billableUnits(a.durationSeconds))}</div>
          <span className={ST_NOT_BILLABLE} title={NOT_BILLABLE_TIP}>Not billable</span>
          <span className="text-[11px] text-casevo-text-soft">No case linked</span>
        </>
      )
    }
    return (
      <>
        <div className="text-[15px] font-semibold tracking-[-0.01em] text-casevo-forest tabular-nums">{a.amountPence == null ? <WithheldAmount size={13} /> : money(a.amountPence)}</div>
        <div className="text-[12px] text-casevo-text-soft tabular-nums">{mins(a.durationSeconds)} · {unitsLabel(billableUnits(a.durationSeconds))}</div>
        {p.isAdmin && a.gradeAtEntry && (
          <div className="text-[11.5px] text-casevo-text-soft tabular-nums">Grade {a.gradeAtEntry}{a.hourlyRatePenceAtEntry != null ? ` · ${money(a.hourlyRatePenceAtEntry)}/h` : ''}</div>
        )}
        {a.billed ? <span className={ST_BILLED}><UiIconSvg icon="lock" size={11} />Billed</span> : <span className={ST_WIP}>Unbilled WIP</span>}
      </>
    )
  }

  const actions = (e: FeedEntry): React.ReactNode => {
    // Billing entries are read-only here — the whole entry opens the bill.
    if (e.kind === 'bill') return null
    if (e.kind === 'document') {
      const d = e.d
      if (isLockedDoc(d)) {
        return <span className="w-[30px] h-[30px] grid place-items-center text-casevo-text-soft" title={LOCKED_DOC_TIP} aria-label={LOCKED_DOC_TIP}><UiIconSvg icon="lock" size={14} /></span>
      }
      const busy = p.downloadingDocId === d.id || p.deletingDocId === d.id
      return (
        <>
          <button type="button" className={IBTN} title="Preview" aria-label={`Preview ${d.filename}`} disabled={busy} onClick={() => p.onOpenDocument(d)}><UiIconSvg icon="eye" /></button>
          <button type="button" className={IBTN} title="Download" aria-label={`Download ${d.filename}`} disabled={busy} onClick={(ev) => { ev.stopPropagation(); p.onDownloadDocument(d) }}><UiIconSvg icon="dl" /></button>
{/* The delivered bill PDF can't be deleted (server 409) — no Delete for it. */}
          {/* (Audit batch 3) …nor the proof of a client instruction (server 409). */}
          {d.proofFor && <span className="w-[30px] h-[30px] grid place-items-center text-casevo-text-soft" role="img" title={PROOF_LOCKED} aria-label={`Can't delete: ${PROOF_LOCKED}`}><UiIconSvg icon="lock" size={14} /></span>}
          {!d.deliveredBillPdf && !d.esigned && !d.proofFor && canDeleteDoc(d.uploadedBy?.id) && (
                    <button type="button" className={`${IBTN} hover:!bg-[#fdecec] hover:!text-[#b42318] dark:hover:!bg-[rgba(127,29,29,0.35)] dark:hover:!text-casevo-danger`} title="Delete" aria-label={`Delete ${d.filename}`} disabled={busy} onClick={() => p.onDeleteDocument(d)}><UiIconSvg icon="trash" /></button>
          )}
        </>
      )
    }
    const a = e.a
    const mod = p.canModify(a)
    const busy = p.deletingActivityId === a.id
    return (
      <>
        {/* (No eye: the whole card / ledger row opens the read-only view.) */}
        {mod && !a.billed && (
          <>
            <button type="button" className={IBTN} title={a.actionType === 'email' ? 'Edit time (the sent email itself is locked)' : 'Edit'} aria-label={a.actionType === 'email' ? 'Edit time' : 'Edit entry'} disabled={busy} onClick={() => p.onEditActivity(a)}><UiIconSvg icon="edit" /></button>
            {/* A sent email can't be deleted (server 409 SENT_EMAIL_RECORD) — no Delete for it. */}
            {a.actionType !== 'email' && <button type="button" className={`${IBTN} hover:!bg-[#fdecec] hover:!text-[#b42318] dark:hover:!bg-[rgba(127,29,29,0.35)] dark:hover:!text-casevo-danger`} title={busy ? 'Deleting…' : 'Delete'} aria-label="Delete entry" disabled={busy} onClick={() => p.onDeleteActivity(a)}><UiIconSvg icon="trash" /></button>}
          </>
        )}
        {a.billed && <span className="w-[30px] h-[30px] grid place-items-center text-casevo-text-soft" title="Billed entries are locked"><UiIconSvg icon="lock" size={14} /></span>}
      </>
    )
  }

  // "View PDF" on an activity that carries a PDF (e.g. the exact bill PDF a
  // Send bill emailed) — opens it straight from the card, via the SAME helper
  // as ActivityViewModal's File row (case-access-checked signed URL).
  const pdfButton = (a: Activity): React.ReactNode => {
    const f = a.attachment
    if (!f || f.mimeType !== 'application/pdf') return null
    const err = pdfError?.id === a.id ? pdfError.msg : null
    return (
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <button
          type="button"
          title={`Open ${f.filename}`}
          onClick={(ev) => {
            ev.stopPropagation()
            setPdfError(null)
            openFileById(f.id).catch((x) => setPdfError({ id: a.id, msg: (x as { response?: { data?: { error?: string } } })?.response?.data?.error || (x instanceof Error && x.message) || 'Couldn’t open that document' }))
          }}
          onKeyDown={(ev) => ev.stopPropagation()}
          className="inline-flex items-center gap-1.5 h-7 px-[10px] rounded-[8px] border border-casevo-border bg-casevo-surface text-[12.5px] font-medium text-casevo-brand dark:text-[#8fd6b0] hover:border-casevo-mid hover:bg-casevo-mint/40 transition-colors focus:outline-none casevo-focus"
        >
          <UiIconSvg icon="eye" size={13} />View PDF
        </button>
        {err && <span role="alert" className="text-[12px] text-casevo-danger">{err}</span>}
      </div>
    )
  }

  const groupTotal = (items: FeedEntry[]): React.ReactNode => {
    const t = billSum(items)
    if (!t.secs && !t.pence) return null
    return <span className="text-[12px] text-casevo-text-soft tabular-nums"><b className="font-semibold text-casevo-forest">{fmtUnits(t.tenths)}</b> units · <b className="font-semibold text-casevo-forest">{money(t.pence)}</b></span>
  }

  // Type tile. For a DOCUMENT the (green/teal) tile is the download button —
  // it reads as one, so it must act as one: same access-checked signed-URL →
  // blob flow as the Download action. Locked docs keep a static tile.
  const entryTile = (e: FeedEntry, cls: string, iconSize: number): React.ReactNode => {
    const face = <EntryIconSvg icon={e.def.icon} size={iconSize} />
    if (e.kind !== 'document') return <div className={`${cls} ${tone(e.def.key)}`}>{face}</div>
    const d = e.d
    if (isLockedDoc(d)) return <div className={`${cls} ${tone(e.def.key)} opacity-60`} title={LOCKED_DOC_TIP}>{face}</div>
    const busy = p.downloadingDocId === d.id || p.deletingDocId === d.id
    return (
      <button
        type="button"
        className={`${cls} ${tone(e.def.key)} hover:brightness-95 dark:hover:brightness-125 disabled:opacity-50 disabled:cursor-wait focus:outline-none casevo-focus`}
        title={`Download ${d.filename}`}
        aria-label={`Download ${d.filename}`}
        disabled={busy}
        onClick={(ev) => { ev.stopPropagation(); p.onDownloadDocument(d) }}
        onKeyDown={(ev) => ev.stopPropagation()}
      >{face}</button>
    )
  }

  // ── Layout A · rich cards ──
  const renderCards = (): React.ReactNode => groups.map((g) => (
    <section key={g.key} aria-label={g.title}>
      <div className="flex items-center gap-[10px] mt-[22px] mb-[10px] mx-[2px]">
        <h3 className="m-0 text-[11.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft">{g.title}</h3>
        <span className="flex-1 h-px bg-casevo-border-soft" />
        {groupTotal(g.items)}
      </div>
      {g.items.map((e) => {
        const title = entryTitle(e)
        const dl = detailLine(e)
        const slot = billSlot(e)
        // Clickable as a whole: an activity → its read-only view; a billing
        // entry → that bill (utils/openBill — opens it, never changes it).
        const openWhole = e.kind === 'activity' ? () => p.onViewActivity(e.a) : e.kind === 'bill' ? () => { void openBillById(e.b.billId) } : null
        return (
          // ★ An ACTIVITY card is clickable as a whole → the read-only view
          // (the ledger row already was; in Cards only the title + a hover
          // eye did). Inner controls stop propagation, so the pencil / delete
          // / case chip never double-trigger; a text selection doesn't open.
          <article
            key={e.id}
            {...(openWhole ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': e.kind === 'bill' ? `Open ${title}` : `View ${title}`,
              onClick: () => { if (!window.getSelection()?.toString()) openWhole() },
              onKeyDown: (ev: React.KeyboardEvent) => {
                if (ev.target !== ev.currentTarget) return
                if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); openWhole() }
              },
            } : {})}
            className={`${CARD} group relative grid grid-cols-[36px_minmax(0,1fr)_auto] gap-[14px] px-[18px] py-4 mb-[10px] hover:border-casevo-mid transition-colors ${openWhole ? 'cursor-pointer focus:outline-none casevo-focus' : ''}`}
          >
            {entryTile(e, 'w-9 h-9 rounded-[10px] grid place-items-center', 18)}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {typePill(e.def)}
                {e.kind === 'document' ? caseChip(e.d.caseId, { name: e.d.caseName, ref: e.d.caseRef, locked: isLockedDoc(e.d) }) : caseChip(e.kind === 'activity' ? e.a.caseId : e.b.caseId)}
              </div>
              {e.kind !== 'document'
                ? <button type="button" tabIndex={-1} onClick={(ev) => { ev.stopPropagation(); openWhole?.() }} className="block text-left mt-1 text-[14.5px] font-semibold text-casevo-forest hover:underline focus:outline-none casevo-focus rounded-sm break-words">{title}</button>
                : isLockedDoc(e.d)
                  ? <span className="block mt-1 text-[14.5px] font-semibold text-casevo-text-soft break-all" title={LOCKED_DOC_TIP}>{title}</span>
                  : <button type="button" onClick={() => p.onOpenDocument(e.d)} className="block text-left mt-1 text-[14.5px] font-semibold text-casevo-forest hover:underline focus:outline-none casevo-focus rounded-sm break-all">{title}</button>}
              <div className="flex items-center gap-2 flex-wrap text-[12.5px] text-casevo-text-soft mt-[6px] min-w-0">
                {byline(e)}
                <span aria-hidden="true">·</span>
                <span className="tabular-nums" title={new Date(e.at).toLocaleString('en-GB')}>{whenLabel(e.at, g.kind)}</span>
              </div>
              {dl && <div className="text-[12.5px] text-casevo-text-soft mt-[5px]">{dl}</div>}
              {e.kind === 'activity' && e.a.content && <p className="text-[13px] text-casevo-text-soft mt-2 leading-[1.55] whitespace-pre-wrap">{e.a.content}</p>}
              {e.kind === 'activity' && pdfButton(e.a)}
            </div>
            {/* The hover actions sit IN the right column's flow, pinned to its
                foot — never on top of the £ / "Unbilled WIP" status (a short
                card, e.g. no note, reserves the row for them instead). */}
            <div className="text-right flex flex-col items-end gap-1 min-w-[118px]">
              {slot}
              <div onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()} className="mt-auto pt-1 flex gap-[2px] sm:opacity-0 sm:group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity">{actions(e)}</div>
            </div>
          </article>
        )
      })}
    </section>
  ))

  // ── Layout B · rail ledger ──
  const LEDGER_COLS = 'grid grid-cols-[72px_24px_minmax(0,1fr)_132px_84px_96px_96px] gap-[10px]'
  const renderLedger = (): React.ReactNode => (
    <div className={`${CARD} py-[6px] mt-1 overflow-x-auto`}>
      <div className="min-w-[760px]">
        <div className={`${LEDGER_COLS} px-4 py-2 text-[10.5px] font-bold tracking-[0.12em] uppercase text-casevo-text-soft border-b border-casevo-border-soft`}>
          <span>When</span><span /><span>Entry</span><span>By</span><span className="text-right">Units</span><span className="text-right">Amount</span><span>Status</span>
        </div>
        {groups.map((g) => (
          <div key={g.key}>
            <div className="flex items-center gap-[10px] px-4 pt-[14px] pb-[6px] text-[11.5px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft">
              {g.title}<span className="ml-auto normal-case tracking-normal font-medium">{groupTotal(g.items)}</span>
            </div>
            {g.items.map((e) => {
              const billable = e.kind === 'activity' && isBillable(e.a, e.def)
              const nonBillable = e.kind === 'activity' && isCaselessTime(e.a, e.def)
              const title = entryTitle(e)
              const dl = detailLine(e)
              const locked = e.kind === 'document' && isLockedDoc(e.d)
              // A billing entry opens its bill (utils/openBill — read-only).
              const open = (): void => { if (e.kind === 'activity') p.onViewActivity(e.a); else if (e.kind === 'bill') void openBillById(e.b.billId); else if (!locked) p.onOpenDocument(e.d) }
              return (
                <div
                  key={e.id}
                  role="button"
                  tabIndex={0}
                  onClick={open}
                  onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open() } }}
                  className={`${LEDGER_COLS} items-center px-4 min-h-[46px] ${locked ? 'cursor-default' : 'cursor-pointer'} hover:bg-casevo-input focus:outline-none casevo-focus`}
                  aria-label={`${e.def.label}: ${title}${locked ? ` (${LOCKED_DOC_TIP})` : ''}`}
                  aria-disabled={locked || undefined}
                  title={locked ? LOCKED_DOC_TIP : undefined}
                >
                  <span className="text-[12.5px] text-casevo-text-soft tabular-nums">{whenLabel(e.at, g.kind)}</span>
                  <span className="relative h-full grid place-items-center before:content-[''] before:absolute before:top-0 before:bottom-0 before:left-1/2 before:w-[2px] before:-ml-px before:bg-casevo-border-soft">
                    {entryTile(e, 'relative w-6 h-6 rounded-[7px] grid place-items-center', 13)}
                  </span>
                  <span className="min-w-0 flex items-center gap-2">
                    {typePill(e.def)}
                    <span className="truncate text-[13.5px] text-casevo-forest"><b className="font-semibold">{title}</b>{dl && <span className="text-casevo-text-soft"> · {dl}</span>}</span>
                  </span>
                  <span className="flex items-center gap-[7px] text-[12.5px] text-casevo-text-soft min-w-0">
                    {byline(e, false)}
                  </span>
                  <span className="text-right text-[12.5px] text-casevo-text-soft tabular-nums">
                    {billable || nonBillable ? billableUnits(e.kind === 'activity' ? e.a.durationSeconds : 0) : <span className="text-casevo-border-soft">—</span>}
                    {billable && p.isAdmin && e.a.gradeAtEntry && <span className="block text-[10.5px] whitespace-nowrap">Grade {e.a.gradeAtEntry}{e.a.hourlyRatePenceAtEntry != null ? ` £${Math.round(e.a.hourlyRatePenceAtEntry / 100)}` : ''}</span>}
                  </span>
                  <span className="text-right text-[13.5px] font-semibold text-casevo-forest tabular-nums">{billable ? (e.a.amountPence == null ? <WithheldAmount /> : money(e.a.amountPence)) : e.kind === 'bill' ? billMoneyNode(e.b) : <span className="font-normal text-casevo-border-soft">—</span>}</span>
                  <span className="flex items-center">
                    {billable
                      ? (e.a.billed ? <span className={ST_BILLED}><UiIconSvg icon="lock" size={11} />Billed</span> : <span className={ST_WIP}>WIP</span>)
                      // Case-less time: saved, never billable.
                      : nonBillable
                        ? <span className={ST_NOT_BILLABLE} title={NOT_BILLABLE_TIP}>Not billable</span>
                      // Documents: the Download action lives here in the ledger
                      // (the Status column is otherwise empty for them).
                      : e.kind === 'document'
                        // Own click/keys — the row itself is a button (open).
                        ? <span className="flex gap-[2px]" onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()}>{actions(e)}</span>
                        // Billing: what it is (masked £ sits in Amount).
                        : e.kind === 'bill'
                          ? <span className={e.b.kind === 'invoice' ? ST_BILLED : ST_WIP}>{e.b.kind === 'invoice' ? (e.b.status === 'VOID' ? 'Voided' : 'Issued') : e.b.kind === 'payment' ? 'Received' : 'Credited'}</span>
                          : null}
                  </span>
                </div>
              )
            })}
          </div>
        ))}
        <div className="flex justify-end gap-[18px] px-4 py-3 mt-1 border-t border-casevo-border-soft text-[13px] text-casevo-text-soft tabular-nums">
          <span>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
          <span><b className="font-semibold text-casevo-forest">{fmtUnits(visibleTotal.tenths)}</b> units</span>
          <span>Billable <b className="font-semibold text-casevo-forest">{money(visibleTotal.pence)}</b>{kpi.withheld && <span className="ml-1 text-casevo-text-soft" title="Your entries only — colleagues’ £ is hidden">(yours)</span>}</span>
        </div>
      </div>
    </div>
  )

  // ★ NO LAYOUT SHIFT: everything the feed shows above / inside the list —
  // entries, documents, billing entries (merged into the list) and the drafts
  // strip — loads in parallel and appears in ONE go; until then the KPI row
  // and the list show same-shape skeletons.
  const loading = p.activities === null || p.documents === null || p.billEvents === null || !!p.draftsLoading
  // (audit) A load ERROR must never read as an empty client — the error
  // banners only render in the non-empty branch below.
  const nothingAtAll = !loading && kpi.count === 0 && !serverFiltersOn && p.errors.every((e) => !e)

  // ── header (title + layout toggle + actions) ──
  const header = (
    <div className="flex items-end gap-3 mb-[14px] flex-wrap">
      <div className="flex-1 min-w-[220px]">
        {/* Case page: the shell's group eyebrow + status (mockup .shead .e). */}
        {scoped && frame && (
          <div className="flex items-center gap-[8px] mb-[3px] text-[11px] tracking-[0.16em] uppercase font-bold text-casevo-text-soft">{frame.group}{frame.chip}</div>
        )}
        <h2 className="m-0 text-[20px] font-semibold tracking-[-0.01em] text-casevo-forest">Activity</h2>
        <div className="text-[13px] text-casevo-text-soft mt-0.5">
          {scoped ? 'Billable time, emails, documents and billing on this case.' : <>Billable work, correspondence, documents and billing for {p.clientFirstName || 'this client'} — newest first.</>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {!scoped && !nothingAtAll && (
          <div className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface" role="group" aria-label="Layout">
            {([['cards', 'Cards', 'cards'], ['ledger', 'Ledger', 'rows']] as const).map(([k, label, icon]) => (
              <button key={k} type="button" aria-pressed={layout === k} onClick={() => chooseLayout(k)}
                className={`inline-flex items-center gap-1.5 h-9 px-3 text-[13px] border-r border-casevo-border-soft last:border-r-0 focus:outline-none casevo-focus ${layout === k ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`}>
                <UiIconSvg icon={icon} size={14} />{label}
              </button>
            ))}
          </div>
        )}
        {scoped
          // Case page: uploads live in the Documents section (mockup: Send email + Log time).
          ? <button type="button" className={BTN_SECONDARY} onClick={p.onSendEmail}><EntryIconSvg icon="mail" size={15} />Send email</button>
          : <button type="button" className={BTN_SECONDARY} onClick={p.onUpload}><EntryIconSvg icon="upload" size={15} />Upload document</button>}
        {/* New invoice — the SAME action as the Log-time menu's Invoice tile
            (onCreateInvoice): this case's Raise bill, or on the client-wide
            tab, pick the case first (ChooseBillCaseModal). It only opens the
            draft flow — issuing / sending stay on the bill. */}
        {p.onCreateInvoice && (
          <button type="button" className={BTN_SECONDARY} onClick={p.onCreateInvoice} title={scoped ? 'Raise a bill for this case' : 'Choose a case, then raise a bill'}>
            <EntryIconSvg icon="invoice" size={15} />New invoice
          </button>
        )}
        {/* Client statement — a covering summary of the client's issued bills
            across their cases (on demand; writes nothing). Client tab only. */}
        {!scoped && p.onStatement && (
          <button type="button" className={BTN_SECONDARY} onClick={p.onStatement} title="A statement of this client’s issued bills, across their cases">
            {/* A document (a statement of account) — distinct from the receipt-shaped
                New invoice icon and the Ledger toggle's list icon. */}
            <EntryIconSvg icon="letter" size={15} />Statement
          </button>
        )}
        {/* Log time → the light type POPOVER (approved Option A). It merges
            the old split button's chevron menu + the full-modal picker step
            it opened: the tiles pick a type straight into the timed form. */}
        <LogTimeButton
          className={`${BTN_PRIMARY} !gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px] border border-casevo-primary`}
          onPick={p.onLogVariant}
          onEmail={p.onSendEmail}
          onCreateInvoice={p.onCreateInvoice} invoiceNeedsCase={!scoped}
        />
      </div>
    </div>
  )

  // ── KPI tiles ──
  const KPI_TILE = `${CARD} px-4 py-[14px]`
  const KL = 'text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft'
  const KV = 'text-[22px] font-semibold tracking-[-0.01em] text-casevo-forest mt-1 tabular-nums'
  const KS = 'text-[12.5px] text-casevo-text-soft mt-px'
  const billedPct = kpi.mPence > 0 ? Math.round((kpi.mBilled / kpi.mPence) * 100) : 0
  const kpis = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-[14px]">
      <div className={KPI_TILE}>
        <div className={KL}>Billable this month</div>
        <div className={KV}>{money(kpi.mPence)}{kpi.withheld && <span className="ml-1 align-middle text-[12px] font-normal text-casevo-text-soft" title="Your entries only — colleagues’ £ is hidden">(yours)</span>}</div>
        <div className={`${KS} tabular-nums`}>{fmtUnits(kpi.mTenths)} units · {mins(kpi.mSecs)}</div>
        <div className="flex h-1.5 rounded-[4px] bg-casevo-border-soft mt-[10px] overflow-hidden" aria-hidden="true">
          <i className="block h-full bg-casevo-brand" style={{ width: `${billedPct}%` }} />
          <i className="block h-full bg-casevo-light" style={{ width: `${kpi.mPence > 0 ? 100 - billedPct : 0}%` }} />
        </div>
      </div>
      <div className={KPI_TILE}>
        <div className={KL}>Unbilled WIP</div>
        <div className={KV}>{money(kpi.wip)}{kpi.withheld && <span className="ml-1 align-middle text-[12px] font-normal text-casevo-text-soft" title="Your entries only — colleagues’ £ is hidden">(yours)</span>}</div>
        <div className={KS}>not yet invoiced</div>
      </div>
      {/* Bills of costs issued this month (same period as "Billable this month")
          + payments received this month — from the server-masked billing
          entries, so a partial / hidden figure carries the lock + "partial". */}
      <div className={KPI_TILE}>
        <div className={KL}>Invoiced this month</div>
        <div className={KV}>{p.billEvents === null ? <span className="text-casevo-text-soft">—</span> : partialMoney(kpi.invPence, kpi.invPartial, 15)}</div>
        <div className={`${KS} tabular-nums`}>
          {kpi.invCount} {kpi.invCount === 1 ? 'bill' : 'bills'} · {partialMoney(kpi.paidPence, kpi.paidPartial)} received
        </div>
      </div>
      <div className={KPI_TILE}>
        <div className={KL}>Entries</div>
        <div className={KV}>{kpi.count}</div>
        <div className={KS}>{kpi.time} time · {kpi.corr} email · {kpi.docs} docs{kpi.bills ? ` · ${kpi.bills} billing` : ''}</div>
      </div>
    </div>
  )

  // ── toolbar ──
  const toolbar = (
    <div className={`${CARD} flex items-center gap-2 flex-wrap px-3 py-[10px] mb-4`}>
      <div className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface flex-wrap" role="group" aria-label="Filter by type">
        {ENTRY_FAMILIES.map((f) => {
          const on = family === f.key
          if (!f.live) {
            return (
              <button key={f.key} type="button" disabled aria-disabled="true" title="Coming soon"
                className="inline-flex items-center gap-1.5 h-8 px-3 text-[13px] text-casevo-text-soft opacity-60 cursor-not-allowed border-r border-casevo-border-soft last:border-r-0">
                {f.label}<span className={SOON}>Soon</span>
              </button>
            )
          }
          return (
            <button key={f.key} type="button" aria-pressed={on} onClick={() => pickFamily(f.key)}
              className={`inline-flex items-center gap-1.5 h-8 px-3 text-[13px] border-r border-casevo-border-soft last:border-r-0 focus:outline-none casevo-focus ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`}>
              {f.label}<span className="text-[11px] opacity-75 tabular-nums">{famCount(f.key)}</span>
            </button>
          )
        })}
      </div>
      <label className={FLD}>
        <UiIconSvg icon="cal" size={14} />
        <select aria-label="Date range" value={range} onChange={(e) => setRange(e.target.value as Range)} className={SELECT}>
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <UiIconSvg icon="chev" size={12} />
      </label>
      {scoped ? (
        // Fixed to this case (mockup .fld.lockd) — not a filter you can change here.
        <span className={`${FLD} !bg-casevo-mint !border-[color-mix(in_srgb,var(--casevo-brand)_35%,transparent)] font-semibold dark:!text-[#cfeedd]`} title="Activity on this case only">
          <UiIconSvg icon="case" size={14} />This case
        </span>
      ) : p.cases.length > 0 && (
        <label className={FLD}>
          <UiIconSvg icon="case" size={14} />
          <select aria-label="Filter by case" value={p.caseFilter} onChange={(e) => p.onCaseFilter(e.target.value)} className={`${SELECT} max-w-[132px]`}>
            <option value="">All cases</option>
            {p.cases.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.ref}</option>)}
          </select>
          <UiIconSvg icon="chev" size={12} />
        </label>
      )}
      <label className={FLD}>
        <UiIconSvg icon="user" size={14} />
        <select aria-label="Filter by person" value={person} onChange={(e) => setPerson(e.target.value)} className={`${SELECT} max-w-[120px]`}>
          <option value="">Anyone</option>
          {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <UiIconSvg icon="chev" size={12} />
      </label>
      {corrOptions.types.length > 0 && (
        <label className={FLD}>
          <span className="text-[12.5px] text-casevo-text-soft">With</span>
          <select aria-label="Filter by correspondent" value={p.corrFilter ?? '__all__'} onChange={(e) => p.onCorrFilter(e.target.value === '__all__' ? null : e.target.value)} className={`${SELECT} max-w-[110px]`}>
            <option value="__all__">Anyone</option>
            {corrOptions.types.map((t) => <option key={t} value={t}>{t}</option>)}
            {corrOptions.hasUntagged && <option value="">No correspondent</option>}
          </select>
          <UiIconSvg icon="chev" size={12} />
        </label>
      )}
      <label className={`${FLD} flex-1 min-w-[160px]`}>
        <UiIconSvg icon="search" size={14} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activity, correspondents, documents" aria-label="Search activity"
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[13px] text-casevo-forest placeholder:text-casevo-text-soft" />
      </label>
      {scoped && (
        // Cards / Ledger (mockup .lay) — same remembered choice as the client page.
        <div className="inline-flex border border-casevo-border rounded-[9px] overflow-hidden" role="group" aria-label="Layout">
          {([['cards', 'Cards', 'cards'], ['ledger', 'Ledger', 'rows']] as const).map(([k, label, icon]) => (
            <button key={k} type="button" aria-pressed={layout === k} aria-label={label} title={label} onClick={() => chooseLayout(k)}
              className={`w-[34px] h-[30px] grid place-items-center focus:outline-none casevo-focus ${layout === k ? 'bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5]' : 'text-casevo-text-soft hover:bg-casevo-mint/40'}`}>
              <UiIconSvg icon={icon} size={15} />
            </button>
          ))}
        </div>
      )}
      {family === 'time' && (
        <div className="basis-full flex items-center gap-1.5 flex-wrap pt-1" role="group" aria-label="Time entry type">
          {[{ v: 'all', l: 'All time' }, ...ACTION_TYPES.filter((t) => entryTypeFor(t.value).family === 'time').map((t) => ({ v: t.value as string, l: t.label }))].map((c) => (
            <button key={c.v} type="button" aria-pressed={p.typeFilter === c.v} onClick={() => p.onTypeFilter(c.v)}
              className={`h-7 px-[10px] rounded-[8px] text-[12.5px] border focus:outline-none casevo-focus ${p.typeFilter === c.v ? 'bg-casevo-mint border-casevo-brand/30 text-casevo-forest font-semibold' : 'border-transparent text-casevo-mid hover:bg-casevo-mint/40'}`}>
              {c.l}
            </button>
          ))}
        </div>
      )}
      {(clientFiltersOn || serverFiltersOn) && (
        <button type="button" onClick={clearAll} className="text-[12.5px] text-casevo-brand hover:underline focus:outline-none casevo-focus rounded-sm">Clear filters</button>
      )}
    </div>
  )

  // The KPI row's placeholder: the same four tiles (real labels, the value /
  // sub lines as bars inside the real line wrappers, the first tile's bar).
  const kpiSkelLine = (wrap: string, bar: string): React.ReactNode => (
    <div className={wrap}><span className={`${SKELETON_BAR} ${bar}`} /></div>
  )
  const kpisSkeleton = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-[14px]" aria-hidden="true">
      {['Billable this month', 'Unbilled WIP', 'Invoiced this month', 'Entries'].map((label, i) => (
        <div key={label} className={KPI_TILE}>
          <div className={KL}>{label}</div>
          {kpiSkelLine(KV, 'h-4 w-20')}
          {kpiSkelLine(KS, 'h-2.5 w-28')}
          {i === 0 && <div className="h-1.5 rounded-[4px] bg-casevo-border-soft mt-[10px]" />}
        </div>
      ))}
    </div>
  )

  // ── drafts strip (unchanged behaviour: hidden when empty) ──
  const draftsStrip = p.drafts.length > 0 && (
    <div className={`${CARD} flex items-center gap-[10px] flex-wrap px-[14px] py-[10px] mb-4`}>
      <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft">Drafts · {p.drafts.length}</span>
      {p.drafts.map((d) => {
        const when = new Date(d.updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
        return (
          <span key={d.id} className="inline-flex items-center gap-2 h-[30px] pl-[10px] pr-1 rounded-[8px] border border-dashed border-casevo-border bg-casevo-surface text-[12.5px] text-casevo-forest max-w-full">
            <button type="button" onClick={() => p.onResumeDraft(d)} className="inline-flex items-center gap-2 min-w-0 hover:underline focus:outline-none casevo-focus rounded-sm" title="Resume draft">
              <EntryIconSvg icon="mail" size={13} /><span className="truncate">{(d.subject && d.subject.trim()) || '(no subject)'} — {(d.to && d.to.trim()) || 'No recipient'} · {when}</span>
            </button>
            <button type="button" aria-label="Delete draft" title="Delete draft" onClick={() => p.onDeleteDraft(d)} className="w-6 h-6 rounded-[6px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-forest focus:outline-none casevo-focus"><UiIconSvg icon="x" size={12} /></button>
          </span>
        )
      })}
    </div>
  )

  const errors = p.errors.filter((e): e is string => !!e)
  const skeleton = (
    <div aria-busy="true" aria-label="Loading activity">
      <div className="flex items-center gap-[10px] mt-[22px] mb-[10px] mx-[2px]"><span className="h-2.5 w-[70px] rounded bg-casevo-border-soft animate-pulse" /><span className="flex-1 h-px bg-casevo-border-soft" /></div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={`${CARD} grid grid-cols-[36px_minmax(0,1fr)_auto] gap-[14px] px-[18px] py-4 mb-[10px]`}>
          <div className="w-9 h-9 rounded-[10px] bg-casevo-border-soft animate-pulse" />
          <div><div className="h-3 w-2/5 rounded bg-casevo-border-soft animate-pulse" /><div className="h-3.5 w-[70%] rounded bg-casevo-border-soft animate-pulse mt-2.5" /><div className="h-2.5 w-1/2 rounded bg-casevo-border-soft animate-pulse mt-2.5" /></div>
          <div><div className="h-4 w-20 rounded bg-casevo-border-soft animate-pulse" /><div className="h-2.5 w-[60px] rounded bg-casevo-border-soft animate-pulse mt-2" /></div>
        </div>
      ))}
    </div>
  )

  const comingSoon = (
    <div className="mt-[18px] rounded-[14px] border border-dashed border-casevo-border px-4 py-3 flex items-center gap-3 flex-wrap" aria-label="Coming to Activity">
      <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft">Coming to Activity</span>
      {FUTURE_ENTRY_TYPES.map((t) => (
        <span key={t.key} className="inline-flex items-center gap-1.5 text-[12.5px] text-casevo-text-soft opacity-80">
          <span className={`w-6 h-6 rounded-[7px] grid place-items-center ${tone(t.key)}`}><EntryIconSvg icon={t.icon} size={12} /></span>{t.label}
        </span>
      ))}
    </div>
  )

  return (
    <div>
      {header}
      {nothingAtAll ? (
        <div className={`${CARD} px-6 py-[52px] text-center`}>
          <div className="w-14 h-14 rounded-[15px] bg-casevo-mint text-casevo-brand grid place-items-center mx-auto mb-[14px]"><UiIconSvg icon="clock" size={26} /></div>
          <h3 className="m-0 mb-1.5 text-[17px] font-semibold text-casevo-forest">{scoped ? 'No activity on this case yet' : <>No activity for {p.clientFirstName || 'this client'} yet</>}</h3>
          <p className="mx-auto mb-4 max-w-[460px] text-[13.5px] text-casevo-text-soft">
            Activity is the <b className="font-semibold text-casevo-forest">billable</b> record — time on calls, letters, memos and emails, plus documents as they arrive. Team notes live in Notes and are never billed.
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <LogTimeButton className={`${BTN_PRIMARY} !gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px]`} onPick={p.onLogVariant} onEmail={p.onSendEmail} onCreateInvoice={p.onCreateInvoice} invoiceNeedsCase={!scoped} align="center" />
            <button type="button" className={BTN_SECONDARY} onClick={p.onSendEmail}><EntryIconSvg icon="mail" size={15} />Send email</button>
            {!scoped && <button type="button" className={BTN_SECONDARY} onClick={p.onUpload}><EntryIconSvg icon="upload" size={15} />Upload document</button>}
          </div>
          {comingSoon}
        </div>
      ) : (
        <>
          {loading ? kpisSkeleton : kpis}
          {toolbar}
          {draftsStrip}
          {errors.map((e, i) => (
            <ErrorNote key={i} className="mb-2">{e}</ErrorNote>
          ))}
          {p.refetching && (
            <div className="mb-2 flex items-center gap-2 text-xs text-casevo-mid italic" aria-live="polite">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-casevo-mid/40 border-t-casevo-mid animate-spin" aria-hidden="true" />Updating…
            </div>
          )}
          <div className={p.refetching ? 'opacity-40 pointer-events-none transition-opacity' : 'transition-opacity'}>
            {loading ? skeleton : entries.length === 0 ? (
              <div className={`${CARD} px-6 py-10 text-center`}>
                <p className="m-0 text-[13.5px] text-casevo-text-soft">No entries match these filters.</p>
                <button type="button" onClick={clearAll} className="mt-2 text-[13px] text-casevo-brand hover:underline focus:outline-none casevo-focus rounded-sm">Clear filters</button>
              </div>
            ) : layout === 'ledger' ? renderLedger() : (
              <>
                {renderCards()}
                <div className="flex justify-end gap-[18px] px-1 pt-2 text-[13px] text-casevo-text-soft tabular-nums">
                  <span>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
                  <span><b className="font-semibold text-casevo-forest">{fmtUnits(visibleTotal.tenths)}</b> units</span>
                  <span>Billable <b className="font-semibold text-casevo-forest">{money(visibleTotal.pence)}</b>{kpi.withheld && <span className="ml-1 text-casevo-text-soft" title="Your entries only — colleagues’ £ is hidden">(yours)</span>}</span>
                </div>
              </>
            )}
            {!loading && comingSoon}
          </div>
        </>
      )}
    </div>
  )
}

export default ClientActivityFeed
